import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useConfig } from "@/lib/useConfig";
import { useCatalog } from "@/state/CatalogContext";
import { fetchAbout, mediaUrl, type AboutContent } from "@/lib/api";
import { DI, PATH, WI } from "@/components/desktop/desktopUi";

/** Desktop "О нас" page. All texts and photos come from the admin; empty fields use the built-in defaults. */
export function DesktopAbout() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { botUsername } = useConfig();
  const { products } = useCatalog();
  const [a, setA] = useState<AboutContent | null>(null);
  useEffect(() => {
    fetchAbout().then(setA).catch(() => setA(null));
  }, []);
  /** Admin text in the current language → Russian → built-in default. */
  const pick = (v: { ru: string; uz: string; en: string } | undefined, fallbackKey: string) => (v?.[locale]?.trim() || v?.ru?.trim() || t(fallbackKey));
  const paragraphs = (text: string) => text.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);
  const fallbackPhoto = products.find((p) => p.category === "coffee")?.images[0];
  const heroImg = a?.heroImage || "/brand/editorial.webp";
  const storyImg = a?.storyImage || fallbackPhoto || "/brand/hero-pack.webp";
  const stats = (a?.stats ?? []).filter((s) => s.value.trim());
  const shownStats = stats.length
    ? stats.map((s) => ({ value: s.value, label: pick(s.label, "") }))
    : [1, 2, 3].map((i) => ({ value: t(`about.stat${i}v`), label: t(`about.stat${i}l`) }));
  const values = [0, 1, 2].map((i) => ({
    title: pick(a?.values?.[i]?.title, `about.value${i + 1}t`),
    text: pick(a?.values?.[i]?.text, `about.value${i + 1}x`),
  }));
  const valueIcons = [PATH.bean, PATH.check, PATH.people];

  return (
    <div className="da">
      <section className="da-hero">
        <div className="da-hero__text">
          <div className="dc-crumbs"><button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button> › <span>{t("desktop.about")}</span></div>
          <div className="dk-eyebrow">{pick(a?.heroEyebrow, "desktop.about")}</div>
          <h1 className="dk-serif">{pick(a?.heroTitle, "about.heroTitle")}</h1>
          {paragraphs(pick(a?.heroText, "about.heroText")).map((x, i) => <p key={i}>{x}</p>)}
          <div className="dh-hero__cta">
            <button className="dk-btn" onClick={() => navigate({ name: "catalog" })}>{t("desktop.buyCoffee")} {DI.arrow}</button>
            <button className="dk-btn dk-btn--ghost" onClick={() => navigate({ name: "lead", source: "site_about" })}>{t("about.forBusiness")}</button>
          </div>
        </div>
        <div className="da-hero__photo" style={{ backgroundImage: `url(${mediaUrl(heroImg)})` }} />
      </section>

      <section className="da-stats">
        <div className="dk-wrap" style={{ gridTemplateColumns: `repeat(${shownStats.length}, 1fr)` }}>
          {shownStats.map((s, i) => (
            <div key={i}><b className="dk-serif">{s.value}</b><span>{s.label}</span></div>
          ))}
        </div>
      </section>

      <section className="dk-wrap da-story">
        <div className="da-story__photo"><img src={mediaUrl(storyImg, 800)} alt="" /></div>
        <div className="da-story__text">
          <div className="dk-eyebrow">{t("about.storyEyebrow")}</div>
          <h2 className="dk-serif">{pick(a?.storyTitle, "about.storyTitle")}</h2>
          {paragraphs(pick(a?.storyText, "about.storyText")).map((x, i) => <p key={i}>{x}</p>)}
        </div>
      </section>

      <section className="dk-wrap">
        <div className="dh-sec dh-sec--center"><h2 className="dk-serif">{t("about.valuesTitle")}</h2></div>
        <div className="da-values">
          {values.map((v, i) => (
            <div key={i} className="da-value">
              <span className="da-value__ic">{WI(valueIcons[i], 34)}</span>
              <h3 className="dk-serif">{v.title}</h3>
              <p>{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {(a?.gallery?.length ?? 0) > 0 && (
        <section className="dk-wrap">
          <div className="dh-sec dh-sec--center"><h2 className="dk-serif">{t("about.galleryTitle")}</h2></div>
          <div className={`da-gallery da-gallery--${Math.min(a!.gallery.length, 6)}`}>
            {a!.gallery.slice(0, 6).map((g, i) => <img key={i} src={mediaUrl(g, 800)} alt="" />)}
          </div>
        </section>
      )}

      <section className="dk-wrap da-visit">
        <div className="da-visit__card">
          <div className="dk-eyebrow">{t("about.visitEyebrow")}</div>
          <h2 className="dk-serif">{t("about.visitTitle")}</h2>
          {pick(a?.contactText, "desktop.pickupText").split("\n").filter(Boolean).map((line, i) => <p key={i}>{line}</p>)}
          <div className="da-visit__links">
            <a className="dk-btn dk-btn--ghost" href="https://yandex.uz/maps/?text=%D0%A2%D0%B0%D1%88%D0%BA%D0%B5%D0%BD%D1%82%2C%20%D0%BF%D1%80%D0%BE%D1%81%D0%BF%D0%B5%D0%BA%D1%82%20%D0%9C%D1%83%D1%81%D1%82%D0%B0%D0%BA%D0%B8%D0%BB%D0%BB%D0%B8%D0%BA%2C%2059%D0%90" target="_blank" rel="noreferrer">{t("about.onMap")}</a>
            {botUsername && <a className="dk-btn dk-btn--ghost" href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer">{t("desktop.writeTelegram")}</a>}
          </div>
        </div>
        <div className="da-visit__b2b">
          <div className="dh-b2b__tag">{t("profile.b2b")}</div>
          <h2 className="dk-serif">{t("desktop.b2bTitle")}</h2>
          <p>{t("desktop.b2bText")}</p>
          <button className="dk-btn dk-btn--light" onClick={() => navigate({ name: "lead", source: "site_about" })}>{t("desktop.b2bCta")} {DI.arrow}</button>
        </div>
      </section>
    </div>
  );
}
